import React, { FC, memo, useState, useEffect } from 'react';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '../Icon';
import { ITechnicalSpecificationsData, ITechnicalSpecifications } from '.';
import { theme } from '../../styles/theme';
import { ModalVersion, isBrandOV, isMarketGB, isBrandAC, isBrandAP, ROLES, isBrandDS } from '../../constants/main';
import { TriggerIcon } from './TriggerIcon';
import { useDispatch } from 'react-redux';
import useTranslations from '@hooks/useTranslations';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import IconButton from '@components/IconButton';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import {useRenderDescription} from "@hooks/useRenderDescription";

interface IProps {
    spec: ITechnicalSpecifications;
    initialOpenState: boolean;
}

export const TechSpecSection: FC<IProps> = memo(({ spec, initialOpenState }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const dispatch = useDispatch();
    const { t } = useTranslations();
    const { renderDescription } = useRenderDescription();
    const infoIconType =
        isBrandAC || isBrandAP || isBrandDS || (isBrandOV && !isMarketGB) ? Icons.InfoCircle : Icons.Info;
    const isTiresLabellingEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_TIRES_LABELLING_ENABLED);

    useEffect(() => {
        if (isExpanded !== initialOpenState) setIsExpanded(initialOpenState);
    }, [initialOpenState]);

    const getSpecItem = (data: ITechnicalSpecificationsData) => {
        const { label, value, tires, description, images } = data;

        return (
            <div className="specItem">
                {label} {value}{' '}
                {(description || (isTiresLabellingEnabled && tires)) && (
                    <IconButton
                        className="infoIcon tires-icon"
                        icon={infoIconType}
                        color={theme.colors.primary}
                        width={16}
                        height={16}
                        role={ROLES.IMG}
                        onClick={() =>
                            dispatch(
                                UIDuck.openModal({
                                    data: {
                                        category: spec?.category,
                                        image: images?.[0]?.['url'],
                                        description: renderDescription(description),
                                        title: label,
                                        ...(isTiresLabellingEnabled &&
                                            tires && {
                                                tiresSubtitle: t('technicalFeatures.tiresDetailModal.title'),
                                                tires: tires?.data,
                                                tiresDescription: t('technicalFeatures.tiresDetailModal.subtitle'),
                                            }),
                                    },
                                    modalType: 'CAR_OPTION_DETAIL',
                                    modalVersion: ModalVersion.v2,
                                })
                            )
                        }
                        dataTestId="SVG_IMG_TYRES_TECHCHARS"
                        altId="svgImgTyresTechChars"
                        altText={t('technicalFeatures.tiresDetailModal.title')}
                    />
                )}
            </div>
        );
    };

    return (
        <div>
            {spec.category.length ? (
                <div className="technicalWrapper">
                    <Collapsible
                        open={initialOpenState}
                        onTriggerClosing={() => setIsExpanded(false)}
                        onTriggerOpening={() => setIsExpanded(true)}
                        trigger={
                            <div className="collapsibleTriggerHeader">
                                <h3 className="featureTitle">{spec.category}</h3>
                                {isBrandOV && !isMarketGB ? (
                                    <TriggerIcon isExpanded={isExpanded} />
                                ) : (
                                    <Icon name={Icons.ChevronDown} color={theme.colors.primary} height={10} />
                                )}
                            </div>
                        }
                        triggerDisabled={initialOpenState}
                    >
                        <ul className="featureList">
                            {spec.data.map((data: ITechnicalSpecificationsData, i: number) => {
                                return <li key={i}>{getSpecItem(data)}</li>;
                            })}
                        </ul>
                    </Collapsible>
                </div>
            ) : (
                <div className="technicalWrapperLight">
                    {spec.data.map((data: ITechnicalSpecificationsData, i: number) => {
                        return getSpecItem(data);
                    })}
                </div>
            )}
        </div>
    );
});
TechSpecSection.displayName = 'TechSpecSection';
